import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Trophy, Loader2, AlertCircle, Plus, Trash2, Sparkles, Pencil, Check, X } from 'lucide-react';
import { getWinThemes, createWinTheme, updateWinTheme, deleteWinTheme, extractWinThemes, WinTheme } from '../../services/workshop';

interface Props {
  sessionId: string;
  projectId?: string;
}

// Minimal capture panel for win themes — story #177. Deliberately simple
// (title/description, add/list/delete) rather than a full edit-modal pattern
// like ADRs get: no entity existed for this before #177, and the plan scoped
// it down to "enough for the win-strategy session to have somewhere to put
// them", not full-featured editing.
//
// Extraction (story #185) mirrors RequirementsPanel.tsx's runAdrExtraction
// exactly: fire the trigger, then dumb-poll getWinThemes every 5s until the
// row count changes or 180s elapses — no task-status endpoint involved,
// same as ADR/NFR/story-per-persona extraction. Auto-commits straight into
// the list, no draft/review step.
export const WinThemesTab: React.FC<Props> = ({ sessionId }) => {
  const [winThemes, setWinThemes] = useState<WinTheme[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [showRerunConfirm, setShowRerunConfirm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const winThemesRef = useRef<WinTheme[]>([]);

  const load = useCallback(async () => {
    try {
      const result = await getWinThemes(sessionId);
      winThemesRef.current = result;
      setWinThemes(result);
    } catch {
      setError('Failed to load win themes');
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    load();
  }, [load]);

  const runExtraction = useCallback(async () => {
    setExtracting(true);
    setShowRerunConfirm(false);
    setError(null);
    try {
      const beforeCount = winThemesRef.current.length;
      await extractWinThemes(sessionId);
      const deadline = Date.now() + 180000;
      const poll = setInterval(async () => {
        try {
          const result = await getWinThemes(sessionId);
          if (result.length !== beforeCount || Date.now() > deadline) {
            clearInterval(poll);
            setExtracting(false);
            winThemesRef.current = result;
            setWinThemes(result);
          }
        } catch {
          if (Date.now() > deadline) { clearInterval(poll); setExtracting(false); }
        }
      }, 5000);
    } catch {
      setError('Failed to start win theme extraction');
      setExtracting(false);
    }
  }, [sessionId]);

  const handleExtractClick = () => {
    if (extracting) return;
    if (winThemes.length > 0) { setShowRerunConfirm(true); return; }
    runExtraction();
  };

  const handleAdd = async () => {
    if (!title.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await createWinTheme(sessionId, { title: title.trim(), description: description.trim() });
      setTitle('');
      setDescription('');
      await load();
    } catch {
      setError('Failed to add win theme');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (winThemeId: string) => {
    try {
      await deleteWinTheme(sessionId, winThemeId);
      await load();
    } catch {
      setError('Failed to delete win theme');
    }
  };

  const startEdit = (wt: WinTheme) => {
    setEditingId(wt.id);
    setEditTitle(wt.title);
    setEditDescription(wt.description);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditTitle('');
    setEditDescription('');
  };

  const saveEdit = async () => {
    if (!editingId || !editTitle.trim()) return;
    setSavingEdit(true);
    setError(null);
    try {
      await updateWinTheme(sessionId, editingId, { title: editTitle.trim(), description: editDescription.trim() });
      setEditingId(null);
      await load();
    } catch {
      setError('Failed to update win theme');
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <div className="p-4 flex flex-col gap-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Trophy size={16} className="text-primary" />
          <span className="text-sm font-bold text-navy">Win Themes</span>
        </div>
        <button
          onClick={handleExtractClick}
          disabled={extracting}
          className="flex items-center gap-1.5 rounded-lg h-8 px-3 bg-primary hover:bg-primary-dark transition-colors text-white text-xs font-bold shadow-sm disabled:opacity-60"
        >
          {extracting ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
          {extracting ? 'Extracting…' : 'Extract Win Themes'}
        </button>
      </div>

      <p className="text-xs text-slate-500 leading-relaxed">
        Capture the strategic themes this bid is being won on — referenced by requirement answers and evidence
        elsewhere in the bid module. Extract from this session's transcript, or add one manually below.
      </p>

      {error && (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2.5">
          <AlertCircle size={14} className="text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-red-700">{error}</p>
        </div>
      )}

      <div className="bg-white border border-[#e2e8f0] rounded-xl p-3 flex flex-col gap-2">
        <input
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder="Win theme title"
          className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
        />
        <textarea
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="Description"
          rows={2}
          className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none"
        />
        <button
          onClick={handleAdd}
          disabled={saving || !title.trim()}
          className="flex items-center justify-center gap-2 rounded-xl h-9 bg-primary hover:bg-primary-dark text-white text-xs font-bold shadow-sm transition-colors disabled:opacity-60"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add win theme
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 size={18} className="animate-spin text-slate-300" />
        </div>
      ) : winThemes.length === 0 ? (
        <p className="text-xs text-slate-400 text-center py-4">No win themes captured yet.</p>
      ) : (
        <div className="flex flex-col gap-2">
          {winThemes.map(wt => editingId === wt.id ? (
            <div key={wt.id} className="bg-white border border-primary/40 rounded-xl p-3 flex flex-col gap-2">
              <input
                value={editTitle}
                onChange={e => setEditTitle(e.target.value)}
                placeholder="Win theme title"
                className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
              />
              <textarea
                value={editDescription}
                onChange={e => setEditDescription(e.target.value)}
                placeholder="Description"
                rows={2}
                className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none"
              />
              <div className="flex items-center gap-2 justify-end">
                <button
                  onClick={cancelEdit}
                  disabled={savingEdit}
                  className="flex items-center gap-1.5 rounded-lg h-8 px-3 border border-slate-200 bg-white text-slate-600 text-xs font-semibold hover:bg-slate-50 transition-colors disabled:opacity-60"
                >
                  <X size={13} /> Cancel
                </button>
                <button
                  onClick={saveEdit}
                  disabled={savingEdit || !editTitle.trim()}
                  className="flex items-center gap-1.5 rounded-lg h-8 px-3 bg-primary hover:bg-primary-dark text-white text-xs font-bold shadow-sm transition-colors disabled:opacity-60"
                >
                  {savingEdit ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Save
                </button>
              </div>
            </div>
          ) : (
            <div key={wt.id} className="group bg-white border border-[#e2e8f0] rounded-xl px-3 py-2.5 flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-navy">{wt.title}</p>
                {wt.description && <p className="text-xs text-slate-500 mt-0.5">{wt.description}</p>}
              </div>
              <button
                onClick={() => startEdit(wt)}
                className="p-1 rounded hover:bg-primary/10 text-slate-300 hover:text-primary transition-colors opacity-0 group-hover:opacity-100"
                title="Edit win theme"
              >
                <Pencil size={14} />
              </button>
              <button
                onClick={() => handleDelete(wt.id)}
                className="p-1 rounded hover:bg-red-50 text-slate-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                title="Delete win theme"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {showRerunConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full mx-4 flex flex-col gap-5">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10">
                <Sparkles size={20} className="text-primary" />
              </div>
              <h2 className="text-lg font-bold text-navy">Re-extract win themes?</h2>
            </div>
            <p className="text-slate-600 text-sm leading-relaxed">
              This session already has <strong>{winThemes.length} win theme{winThemes.length !== 1 ? 's' : ''}</strong>. Re-running extraction will add a fresh set based on the current transcript.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowRerunConfirm(false)}
                className="px-5 py-2.5 rounded-xl border border-slate-200 text-slate-700 text-sm font-bold hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={runExtraction}
                className="px-5 py-2.5 rounded-xl bg-primary hover:bg-primary-dark text-white text-sm font-bold shadow-sm transition-colors"
              >
                Re-extract
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
